/**
 * v2 PonPortsCard — GPON inventory grid with clipboard-on-click
 * cells and an info-icon row expander (goal e).
 *
 * Click any cell to copy its value to the clipboard. Click the
 * info-icon (ⓘ) to expand the row with deeper detail (MAC history,
 * 3 most-recent optical samples, last 5 transcript lines that
 * targeted this ONU). No row-level click action — that would
 * conflict with the clipboard semantics.
 *
 * Reads ``GET /api/chassis/{id}/inventory`` (existing endpoint; the
 * Phase 5.x adapter-parity work made it return ``last_seen_mac`` +
 * ``last_seen_device`` for GPON too, mirroring VDSL).
 *
 * v0.34.0 (WS-G): full-management adapters also get a per-row ⋮ menu
 * (Deprovision / Move / Swap / Replace VNET / Add service) that deep-links into the
 * matching Provisioning → Manual ops form pre-filled with the row's
 * port / display-id / serial via hash params.
 */
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { MoreVertical, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../../../api/client";
import { LocalTime } from "../../../../lib/datetime";
import { Badge } from "../../../components/Badge";
import { CopyableCell } from "../../../components/CopyableCell";
import { InfoExpandButton } from "../../../components/InfoExpandButton";
import { OccupancyIcon } from "../../../components/OccupancyIcon";
import { RefreshLineButton } from "../../../components/RefreshLineButton";
import { gponStateTone, rxTone } from "../../../lib/signalTones";
import {
  analyseChassis,
  analysePort,
  formatVnetDiff,
  vnetDiff,
  vnetSignature,
  type ChassisVnetProfile,
} from "../../../lib/vnetProfile";

type InventoryOnu = {
  display_id: string;
  onu_or_ont_id: string | null;
  serial: string | null;
  state: string | null;
  profile: string | null;
  vendor_extras: Record<string, unknown> | null;
  observed_at: string;
  last_rx_dbm: number | null;
  last_tx_dbm: number | null;
  last_temp_c: number | null;
  last_sampled_at: string | null;
  last_seen_mac: string | null;
  last_seen_device: string | null;
  occupied: boolean | null;
  missing_scans?: number | null;
};

type InventoryPort = {
  port: string;
  onu_count: number;
  onus: InventoryOnu[];
};

type InventoryResponse = { ports: InventoryPort[] };

export type OnuService = {
  service_name: string;
  vnet_profile: string | null;
  bandwidth_profile: string | null;
  shaper_profile: string | null;
  cosq_profile: string | null;
};
type OnuServicesResp = {
  supported: boolean;
  services: Record<string, OnuService[]>;
  backup_id: number | null;
  backup_taken_at: string | null;
};

/**
 * Per-ONU service assignments, parsed server-side from the LATEST
 * backup. One chassis-wide fetch shared by the conformance summary,
 * every port block, and every expanded row — same query key, so React
 * Query serves them all from one cache entry.
 *
 * ``supported:false`` (non-Radisys adapters) means the whole VNET
 * conformance feature simply does not render.
 */
function useOnuServices(chassisId: string) {
  return useQuery<OnuServicesResp>({
    queryKey: ["v2", "chassis", chassisId, "onu-services"],
    queryFn: async () => {
      const res = await api.get<OnuServicesResp>(
        `/chassis/${chassisId}/onu-services`,
      );
      return res.data;
    },
    // Re-parsing a whole running-config server-side isn't free — only
    // refetch when the backup actually changes (events invalidate the
    // chassis subtree on chassis_backup_taken).
    staleTime: 5 * 60_000,
  });
}

export function PonPortsCard({
  chassisId,
  filter = "",
  rowActions = false,
}: {
  chassisId: string;
  /** Substring filter (serial / display ID / MAC / state) from the
   *  InventoryTab's FilterBar. Empty = show everything. */
  filter?: string;
  /** Show the per-row ⋮ menu (Deprovision / Move / Swap / Replace
   *  VNET). Only full-management adapters — read-only ones have no
   *  Provisioning tab for the deep-links to land on. */
  rowActions?: boolean;
}) {
  const inv = useQuery<InventoryResponse>({
    queryKey: ["v2", "chassis", chassisId, "inventory"],
    queryFn: async () => {
      const res = await api.get<InventoryResponse>(
        `/chassis/${chassisId}/inventory`,
      );
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const needle = filter.trim().toLowerCase();
  const filteredPorts = useMemo(() => {
    const all = inv.data?.ports ?? [];
    if (!needle) return all;
    return all
      .map((p) => ({
        ...p,
        onus: p.onus.filter((o) =>
          [o.serial, o.display_id, o.last_seen_mac, o.state].some((v) =>
            (v ?? "").toLowerCase().includes(needle),
          ),
        ),
      }))
      .filter((p) => p.onus.length > 0)
      .map((p) => ({ ...p, onu_count: p.onus.length }));
  }, [inv.data, needle]);

  const { connectedPorts, offlineGroup } = useMemo(() => {
    // Ubiquiti UFiber convention: ONUs that have previously been
    // seen but aren't currently connected to any PON come back with
    // ``oltPort = 0`` (UFiber doesn't pin ONUs to a specific PON —
    // they can roam between sessions). Split them so the live port
    // grid only renders real PONs, with the offline set tucked into
    // a separate collapsed group. Other vendors (Radisys / Nokia)
    // don't emit a port "0" so this is a no-op for them.
    const connected: InventoryPort[] = [];
    let offline: InventoryPort | null = null;
    for (const p of filteredPorts) {
      if (p.port === "0") offline = p;
      else connected.push(p);
    }
    return { connectedPorts: connected, offlineGroup: offline };
  }, [filteredPorts]);
  const connectedOnus = useMemo(
    () => connectedPorts.reduce((sum, p) => sum + p.onu_count, 0),
    [connectedPorts],
  );
  const offlineOnus = offlineGroup?.onu_count ?? 0;

  // VNET conformance across the connected PONs, from the latest backup.
  const svc = useOnuServices(chassisId);
  const conformance = useMemo(
    () =>
      analyseChassis(
        connectedPorts.map((p) => ({
          port: p.port,
          displayIds: p.onus.map((o) => o.display_id),
        })),
        svc.data?.services,
      ),
    [connectedPorts, svc.data],
  );

  return (
    <section className="rounded-lg bg-slate-900 shadow-border p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
          PON ports &amp; ONUs
        </h2>
        <span className="text-[11px] text-slate-500">
          {connectedPorts.length} port{connectedPorts.length === 1 ? "" : "s"} ·{" "}
          {connectedOnus} connected
          {offlineOnus > 0 ? ` · ${offlineOnus} offline` : ""}
        </span>
      </div>
      {svc.data?.supported && conformance.norm && (
        <VnetBaselineStrip
          c={conformance}
          takenAt={svc.data.backup_taken_at}
        />
      )}
      {inv.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : connectedPorts.length === 0 && offlineGroup == null ? (
        <p className="text-xs text-slate-500">
          {needle && (inv.data?.ports.length ?? 0) > 0 ? (
            "No ONU matches that filter."
          ) : (
            <>
              No inventory yet — run <em>List ONUs</em> from the Run-a-job
              dropdown above.
            </>
          )}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {connectedPorts.map((p) => (
            <PortBlock
              key={p.port}
              port={p}
              chassisId={chassisId}
              rowActions={rowActions}
            />
          ))}
          {offlineGroup && offlineGroup.onu_count > 0 && (
            <PortBlock
              key="offline"
              port={offlineGroup}
              chassisId={chassisId}
              rowActions={rowActions}
              label={`Previously seen · ${offlineGroup.onu_count} offline`}
              initiallyOpen={false}
            />
          )}
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------
// Per-row actions menu — deep-links into Provisioning → Manual ops.
// -----------------------------------------------------------------

const ROW_ACTIONS: { label: string; op: string; scope?: string }[] = [
  { label: "Deprovision", op: "deprovision" },
  { label: "Move", op: "move" },
  { label: "Swap", op: "swap" },
  { label: "Replace VNET", op: "replace-vnet" },
  { label: "Add service", op: "add-service", scope: "onu" },
];

/** Assigning ``location.hash`` fires ``hashchange``; ChassisDetail
 *  routes to Provisioning → Manual ops and ManualOpsPanel seeds the
 *  matching form from the port / display_id / serial params. */
function openManualOp(
  op: string,
  port: string,
  displayId: string | null,
  serial: string | null,
  scope?: string,
) {
  const params = new URLSearchParams({
    tab: "provisioning",
    sub: "manual",
    op,
    port,
  });
  if (displayId) params.set("display_id", displayId);
  if (serial) params.set("serial", serial);
  if (scope) params.set("scope", scope);
  window.location.hash = params.toString();
}

function RowActionsMenu({ port, onu }: { port: string; onu: InventoryOnu }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className="inline-flex items-center rounded p-0.5 text-slate-500 transition-[scale,background-color,color] duration-150 ease-out hover:bg-slate-800 hover:text-slate-200 active:scale-[0.96]"
        aria-label={`Actions for ${onu.display_id}`}
        title="Deprovision / move / swap / replace VNET / add service — opens the form pre-filled"
      >
        <MoreVertical className="h-3.5 w-3.5" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={[
            "z-50 min-w-[10rem] rounded-lg border p-1 shadow-lg",
            "border-slate-700 bg-slate-900 text-slate-200",
          ].join(" ")}
          sideOffset={4}
          align="start"
        >
          {ROW_ACTIONS.map((a) => (
            <DropdownMenu.Item
              key={a.op}
              onSelect={() =>
                openManualOp(a.op, port, onu.display_id, onu.serial, a.scope)
              }
              className={[
                "cursor-pointer rounded px-2 py-1 text-xs outline-none",
                "transition-[background-color,color] duration-150 ease-out",
                "data-[highlighted]:bg-slate-800 data-[highlighted]:text-white",
              ].join(" ")}
            >
              {a.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * The chassis-wide VNET baseline: what a correctly-provisioned ONU on
 * this chassis looks like, and how many disagree.
 *
 * Rendered only when a baseline actually exists (a unique modal
 * signature). A chassis with no clear normal gets no strip rather than
 * a misleading one.
 */
function VnetBaselineStrip({
  c,
  takenAt,
}: {
  c: ChassisVnetProfile;
  takenAt: string | null;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded bg-slate-950/60 px-3 py-2">
      <span className="text-[10px] uppercase tracking-wide text-slate-400">
        VNET baseline
      </span>
      <span className="font-mono text-[11px] text-slate-200">
        {c.norm?.length ? c.norm.join(" + ") : "none"}
      </span>
      {c.outlierCount > 0 ? (
        <Badge tone="warning">
          {c.outlierCount} ONU{c.outlierCount === 1 ? "" : "s"} differ ·{" "}
          {c.portsWithOutliers.length} port
          {c.portsWithOutliers.length === 1 ? "" : "s"}
        </Badge>
      ) : (
        <Badge tone="success">all match</Badge>
      )}
      {c.portsWithOutliers.length > 0 && (
        <span className="font-mono text-[11px] text-amber-300">
          port {c.portsWithOutliers.join(", ")}
        </span>
      )}
      {c.unknownCount > 0 && (
        <span className="text-[10px] text-slate-400">
          {c.unknownCount} not in the backup
        </span>
      )}
      <span className="ml-auto text-[10px] text-slate-400">
        from backup <LocalTime iso={takenAt} />
      </span>
    </div>
  );
}

/**
 * One ONU's VNET count, tinted only when it disagrees with its port.
 *
 * The count is the scannable part — a column of 3s with one 4 in it
 * finds itself. The tooltip carries the actual profile names and what
 * the port expects, so the operator never has to expand the row to
 * learn WHICH VNET is wrong.
 */
function VnetCell({
  sig,
  known,
  isOutlier,
  norm,
}: {
  sig: string[];
  known: boolean;
  isOutlier: boolean;
  norm: string[] | null;
}) {
  if (!known) {
    return (
      <td
        className="px-2 py-1.5 text-right font-mono text-[11px] text-slate-500"
        title="Not present in the latest backup — no VNET verdict."
      >
        —
      </td>
    );
  }
  const title = [
    sig.length ? sig.join(" + ") : "no VNET profiles",
    norm ? `port expects: ${norm.join(" + ") || "no VNET"}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  if (!isOutlier) {
    return (
      <td
        className="px-2 py-1.5 text-right font-mono text-[11px] text-slate-400"
        title={title}
      >
        {sig.length}
      </td>
    );
  }

  // Outliers carry the delta, not just the count — a substituted
  // profile leaves the count unchanged, so "3" alone would look
  // arbitrary next to its conforming neighbours.
  const delta = formatVnetDiff(vnetDiff(sig, norm));
  return (
    <td className="px-2 py-1.5 text-right" title={title}>
      <span className="inline-flex items-center justify-end gap-1.5">
        {delta && (
          <span className="font-mono text-[10px] text-amber-300">{delta}</span>
        )}
        <Badge tone="warning">{sig.length}</Badge>
      </span>
    </td>
  );
}

function PortBlock({
  port,
  chassisId,
  rowActions = false,
  label,
  initiallyOpen = true,
}: {
  port: InventoryPort;
  chassisId: string;
  /** Render the per-ONU ⋮ actions menu (full-management adapters). */
  rowActions?: boolean;
  /** Override for the ``<summary>`` text. Used by the Ubiquiti
   *  "Previously seen / offline" collapsed group. */
  label?: string;
  /** Whether the ``<details>`` starts open. Defaults to true
   *  (the connected PONs); the offline group passes false. */
  initiallyOpen?: boolean;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState<Set<string>>(new Set());

  // Per-port VNET conformance. Shares the chassis-wide services cache
  // entry with the summary strip and the expanded rows.
  const svc = useOnuServices(chassisId);
  const vnets = useMemo(
    () =>
      analysePort(
        port.onus.map((o) => o.display_id),
        svc.data?.services,
      ),
    [port.onus, svc.data],
  );
  const outlierSet = useMemo(() => new Set(vnets.outliers), [vnets.outliers]);
  const showVnets = !!svc.data?.supported;

  // Submit a single-line refresh (refresh_onu) and hand off to the
  // InlineJobTracker. The grid row updates itself when the job's terminal
  // chassis_inventory_updated / chassis_macs_updated events invalidate the
  // inventory query (see useEventStream), so no per-row polling here.
  const refreshOne = async (displayId: string) => {
    const k = `${port.port}|${displayId}`;
    setRefreshing((s) => new Set(s).add(k));
    try {
      const res = await api.post<{ id: string }>(
        `/chassis/${chassisId}/jobs`,
        {
          name: "refresh_onu",
          params: { port: port.port, display_id: displayId },
        },
      );
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: res.data.id, kind: "refresh_onu" },
        }),
      );
    } finally {
      setRefreshing((s) => {
        const n = new Set(s);
        n.delete(k);
        return n;
      });
    }
  };

  return (
    <details
      open={initiallyOpen}
      className="rounded border border-slate-800 bg-slate-950/40"
    >
      <summary className="flex cursor-pointer flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 text-xs uppercase tracking-wide text-slate-400">
        <span>
          {label ??
            `Port ${port.port} · ${port.onu_count} ONU${
              port.onu_count === 1 ? "" : "s"
            }`}
        </span>
        {showVnets && vnets.norm && (
          <>
            <span className="font-mono normal-case text-[11px] text-slate-400">
              {vnets.norm.length ? vnets.norm.join(" + ") : "no VNET"}
            </span>
            {vnets.outliers.length > 0 && (
              <Badge tone="warning">
                {vnets.outliers.length} differ
              </Badge>
            )}
          </>
        )}
        {rowActions && label == null && port.onu_count > 0 && (
          <button
            type="button"
            onClick={(e) => {
              // Inside <summary>: don't let the click toggle the <details>.
              e.preventDefault();
              e.stopPropagation();
              openManualOp("add-service", port.port, null, null, "port");
            }}
            className="ml-auto inline-flex items-center gap-1 rounded border border-slate-700 px-1.5 py-0.5 text-[10px] normal-case tracking-normal text-slate-300 transition-[scale,background-color,color] duration-150 ease-out hover:bg-slate-800 hover:text-slate-100 active:scale-[0.96]"
            title={`Add a service to every ONU on port ${port.port} — opens the form pre-filled`}
          >
            <Plus className="h-3 w-3" />
            Add service
          </button>
        )}
      </summary>
      <table className="w-full text-xs">
        <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className={`${rowActions ? "w-16" : "w-12"} px-2 py-1`}></th>
            <th className="px-2 py-1">Display ID</th>
            <th className="px-2 py-1">Serial</th>
            <th className="px-2 py-1">State</th>
            <th className="px-2 py-1 text-right">RX dBm</th>
            <th className="px-2 py-1 text-right">TX dBm</th>
            {showVnets && (
              <th className="px-2 py-1 text-right" title="VNET profiles on this ONU, from the latest backup">
                VNETs
              </th>
            )}
            <th className="px-2 py-1">Last MAC</th>
            <th className="px-2 py-1">Device</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/60 text-slate-300">
          {port.onus.map((o) => {
            const key = `${port.port}|${o.display_id}`;
            const isExpanded = expanded.has(key);
            const isMissing = (o.missing_scans ?? 0) > 0;
            return (
              <>
                <tr
                  key={`row-${key}`}
                  className={isMissing ? "opacity-60" : ""}
                >
                  <td className="px-2 py-1.5">
                    <div className="flex items-center justify-center gap-1">
                      <InfoExpandButton
                        expanded={isExpanded}
                        label={`Show detail for ${o.display_id}`}
                        onClick={() =>
                          setExpanded((prev) => {
                            const next = new Set(prev);
                            if (next.has(key)) next.delete(key);
                            else next.add(key);
                            return next;
                          })
                        }
                      />
                      <RefreshLineButton
                        busy={refreshing.has(key)}
                        label={`Refresh all stats for ${o.display_id}`}
                        onClick={() => refreshOne(o.display_id)}
                      />
                      {rowActions && (
                        <RowActionsMenu port={port.port} onu={o} />
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <CopyableCell value={o.display_id} />
                  </td>
                  <td className="px-2 py-1.5">
                    <CopyableCell
                      value={o.serial}
                      title="Click to copy the ONU serial"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Badge tone={gponStateTone(o.state, o.missing_scans)}>
                      {isMissing
                        ? `missing (${o.missing_scans}/3)`
                        : o.state ?? "—"}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Badge tone={rxTone(o.last_rx_dbm)}>
                      {o.last_rx_dbm == null
                        ? "—"
                        : o.last_rx_dbm.toFixed(2)}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-[11px]">
                    {o.last_tx_dbm == null ? "—" : o.last_tx_dbm.toFixed(2)}
                  </td>
                  {showVnets && (
                    <VnetCell
                      sig={vnetSignature(svc.data?.services[o.display_id])}
                      known={svc.data?.services[o.display_id] !== undefined}
                      isOutlier={outlierSet.has(o.display_id)}
                      norm={vnets.norm}
                    />
                  )}
                  <td className="px-2 py-1.5">
                    <CopyableCell value={o.last_seen_mac} />
                  </td>
                  <td className="px-2 py-1.5 text-[11px]">
                    {o.last_seen_device ? (
                      <span className="inline-flex items-center gap-1">
                        <OccupancyIcon occupied={o.occupied} />
                        <CopyableCell
                          value={o.last_seen_device}
                          title={
                            o.last_seen_mac
                              ? `Resolved via MAC ${o.last_seen_mac} — PPPoE (if active) takes precedence over portal.`
                              : undefined
                          }
                        />
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                </tr>
                {isExpanded && (
                  <tr key={`exp-${key}`} className="bg-slate-950/60">
                    <td></td>
                    <td
                      colSpan={showVnets ? 8 : 7}
                      className="px-3 py-2 text-[11px] text-slate-400"
                    >
                      <DetailPanel
                        chassisId={chassisId}
                        port={port.port}
                        displayId={o.display_id}
                        onu={o}
                      />
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </details>
  );
}

function DetailPanel({
  chassisId,
  port,
  displayId,
  onu,
}: {
  chassisId: string;
  port: string;
  displayId: string;
  onu: InventoryOnu;
}) {
  // v1 has /api/chassis/{cid}/macs which returns ONU-grouped MACs.
  // Reusing it here means we don't have to add a backend endpoint
  // for the v2 detail surface.
  type MacRow = {
    port: string;
    display_id: string;
    macs: {
      mac_address: string;
      mac_normalised: string;
      vnet_or_vlan: string | null;
      collected_at: string;
    }[];
  };
  const macs = useQuery<{ onus: MacRow[] }>({
    queryKey: ["v2", "chassis", chassisId, "macs"],
    queryFn: async () => {
      const res = await api.get<{ onus: MacRow[] }>(
        `/chassis/${chassisId}/macs`,
      );
      return res.data;
    },
  });
  const macsForThis =
    macs.data?.onus.find((r) => r.port === port && r.display_id === displayId)
      ?.macs ?? [];

  // Radisys-only: per-ONU service assignments (VNET / US bandwidth /
  // DS shaper / cosq) parsed from the LATEST chassis backup. Shares one
  // chassis-wide cache entry with the conformance summary and every
  // other expanded row; ``supported:false`` (non-Radisys) hides the
  // block entirely.
  const svc = useOnuServices(chassisId);
  const servicesForThis = svc.data?.services[displayId] ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end">
        <Link
          to={`/chassis/${chassisId}/onu/${encodeURIComponent(port)}/${encodeURIComponent(displayId)}`}
          className="text-[11px] text-emerald-400 hover:text-emerald-300"
          title="Open the per-ONU detail page with the optical RX/TX history chart"
        >
          Optical chart →
        </Link>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <h4 className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
          Inventory
        </h4>
        <dl className="grid grid-cols-2 gap-1">
          <dt className="text-slate-500">Profile</dt>
          <dd className="font-mono">{onu.profile ?? "—"}</dd>
          <dt className="text-slate-500">ONU ID</dt>
          <dd className="font-mono">{onu.onu_or_ont_id ?? "—"}</dd>
          <dt className="text-slate-500">Last sampled</dt>
          <dd className="font-mono">
            <LocalTime iso={onu.last_sampled_at} />
          </dd>
          <dt className="text-slate-500">Observed at</dt>
          <dd className="font-mono">
            <LocalTime iso={onu.observed_at} />
          </dd>
        </dl>
      </div>
      <div>
        <h4 className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
          MAC history ({macsForThis.length})
        </h4>
        {macsForThis.length === 0 ? (
          <p className="text-slate-500">No MACs collected.</p>
        ) : (
          <ul className="flex flex-col gap-0.5 font-mono">
            {macsForThis.slice(0, 6).map((m, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <CopyableCell value={m.mac_normalised} />
                <span className="text-[10px] text-slate-500">
                  vlan {m.vnet_or_vlan ?? "—"}
                </span>
                <span className="text-[10px] text-slate-600">
                  <LocalTime iso={m.collected_at} />
                </span>
              </li>
            ))}
            {macsForThis.length > 6 && (
              <li className="text-[10px] text-slate-500">
                … {macsForThis.length - 6} more
              </li>
            )}
          </ul>
        )}
      </div>
      </div>
      {svc.data?.supported && (
        <div>
          <h4 className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
            Services ({servicesForThis.length})
            {svc.data.backup_taken_at && (
              <span className="ml-2 normal-case text-slate-600">
                as of backup <LocalTime iso={svc.data.backup_taken_at} />
              </span>
            )}
          </h4>
          {svc.data.backup_id == null ? (
            <p className="text-slate-500">
              No backup parsed yet — take a chassis backup to populate
              service details.
            </p>
          ) : servicesForThis.length === 0 ? (
            <p className="text-slate-500">
              No services for this ONU in the latest backup (it may have
              been provisioned since — take a fresh backup).
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5 font-mono">
              {servicesForThis.map((s) => (
                <li
                  key={s.service_name}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5"
                >
                  <span className="text-slate-200">{s.service_name}</span>
                  <span className="text-[10px] text-slate-500">
                    VNET{" "}
                    <span className="text-emerald-300">
                      {s.vnet_profile ?? "—"}
                    </span>
                  </span>
                  <span className="text-[10px] text-slate-500">
                    US{" "}
                    <span className="text-slate-300">
                      {s.bandwidth_profile ?? "—"}
                    </span>
                  </span>
                  <span className="text-[10px] text-slate-500">
                    DS{" "}
                    <span className="text-slate-300">
                      {s.shaper_profile ?? "—"}
                    </span>
                  </span>
                  <span className="text-[10px] text-slate-500">
                    cosq{" "}
                    <span className="text-slate-300">
                      {s.cosq_profile ?? "—"}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
